import React from 'react';
import { Box, Typography, Button, Divider, Stack, useTheme, IconButton } from '@mui/material';
import { Add, Delete, Person } from '@mui/icons-material';
import SearchableAgencyOfficialDropdown from '@components/SearchableAgencyOfficialDropdown.tsx';
import { useUser } from '@hooks/useUser.tsx';
import { OperationAgencyOfficialAttributes } from '../index.tsx';
import { OPERATION_AGENCY_OFFICIAL_ROLES } from '../../../../constants.ts';

export interface OperationAgencyOfficialsProps {
  incidentCommander: OperationAgencyOfficialAttributes | null;
  deputyIncidentCommanders: OperationAgencyOfficialAttributes[];
  commandStaff: OperationAgencyOfficialAttributes[];
  onIncidentCommanderChange: (official: OperationAgencyOfficialAttributes | null) => void;
  onDeputyIncidentCommandersChange: (id: number, official: OperationAgencyOfficialAttributes | null) => void;
  onAddDeputyIncidentCommander: () => void;
  onDeleteDeputyIncidentCommander: (id: number) => void;
  onClearDeputyIncidentCommander: (id: number) => void;
  onAddCommandStaff: () => void;
  onChangeCommandStaff: (defaultId: number, official: OperationAgencyOfficialAttributes) => void;
  onDeleteCommandStaff: (id: number) => void;
  onClearCommandStaff: (id: number) => void;
}

export default function OperationAgencyOfficials({
  incidentCommander,
  deputyIncidentCommanders,
  commandStaff,
  onIncidentCommanderChange,
  onDeputyIncidentCommandersChange,
  onAddDeputyIncidentCommander,
  onDeleteDeputyIncidentCommander,
  onClearDeputyIncidentCommander,
  onChangeCommandStaff,
  onAddCommandStaff,
  onDeleteCommandStaff,
  onClearCommandStaff,
}: OperationAgencyOfficialsProps) {
  const sectionWidth = 832;
  const theme = useTheme();
  const { user } = useUser();

  return (
    <>
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <Person sx={{ color: theme.palette.text.primary }} />
        <Typography variant="h5" sx={{ flex: 1 }}>
          Command Personnel
        </Typography>
      </Stack>

      <Box
        sx={{
          p: 2,
          backgroundColor: theme.palette.blueGrey[50],
          borderRadius: '10px',
          width: '100%',
          maxWidth: sectionWidth
        }}>
        <Stack spacing={2}>
          <Typography variant="h6">Incident Commander</Typography>
          <React.Fragment key={incidentCommander?.official?.id}>
            <SearchableAgencyOfficialDropdown
              label="Incident Commander"
              accessToken={user?.accessToken || ''}
              value={incidentCommander?.official || null}
              onSelect={(official) => {
                if (!official || !official.id) return;
                onIncidentCommanderChange({ official, assignment: 'Incident Commander' })
              }}
              onError={error => { throw new Error(error) }}
              hideAssignmentField={true}
              onClear={() => onIncidentCommanderChange(null)}
            />
          </React.Fragment>

          <Divider />

          <Typography variant="h6">Deputy Incident Commanders</Typography>
          {deputyIncidentCommanders.map((commander, idx) => {
            return (
              <React.Fragment key={commander?.official?.id}>
                <Stack
                  key={idx}
                  direction="row"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <SearchableAgencyOfficialDropdown
                    label="Deputy Incident Commander"
                    accessToken={user?.accessToken || ''}
                    value={commander?.official || null}
                    onSelect={(official) => {
                      if (!official || !commander.official?.id) return;
                      onDeputyIncidentCommandersChange(commander.official.id, { official, assignment: "Deputy Incident Commander" })
                    }}
                    onError={error => { throw new Error(error) }}
                    hideAssignmentField={true}
                    onClear={(id) => onClearDeputyIncidentCommander(id)}
                  />
                  <IconButton
                    onClick={() => {
                      if (!commander.official?.id) return;
                      onDeleteDeputyIncidentCommander(commander.official.id)
                    }}
                  >
                    <Delete sx={{ color: (theme) => theme.palette.utilityIcon }} />
                  </IconButton>
                </Stack>
              </React.Fragment>
            )
          })}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<Add />}
            onClick={() => onAddDeputyIncidentCommander()}
            sx={{ alignSelf: 'flex-start' }}>
            Deputy Incident Commander
          </Button>

          <Divider />

          <Typography variant="h6">Command Staff</Typography>
          {commandStaff?.map((staff, index) => {
            return (
              <React.Fragment key={staff?.official?.id}>
                <Stack
                  key={index}
                  direction="row"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <SearchableAgencyOfficialDropdown
                    label="Command Staff"
                    accessToken={user?.accessToken || ''}
                    value={staff.official}
                    assignment={staff.assignment}
                    onSelect={(official, assignment) => {
                      if (!official || !staff.official?.id) return;
                      onChangeCommandStaff(
                        staff.official.id,
                        { official, assignment: assignment ?? OPERATION_AGENCY_OFFICIAL_ROLES.COMMAND_STAFF })
                    }}
                    onError={error => { throw new Error(error) }}
                    hideAssignmentField={false}
                    onClear={onClearCommandStaff}
                  />


                  <IconButton
                    onClick={() => {
                      if (!staff.official?.id) return;
                      onDeleteCommandStaff(staff.official.id)
                    }}
                  >
                    <Delete sx={{ color: (theme) => theme.palette.utilityIcon }} />
                  </IconButton>
                </Stack>
                {index < commandStaff.length - 1 && <Divider />}
              </React.Fragment>
            )
          })}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<Add />}
            onClick={() => onAddCommandStaff()}
            sx={{ alignSelf: 'flex-start' }}>
            Command Staff
          </Button>
        </Stack>
      </Box>
    </>
  );
}
